export type CornerKey = "lf" | "rf" | "lr" | "rr";

export type PredictedCorners = {
  lf: number[]; rf: number[]; lr: number[]; rr: number[];
};

export type R0RecordedReference = {
  time_s: number[];
  recorded: PredictedCorners;
};

export type MultiRunOverlayInput = {
  time_s: number[];
  runs: Array<{ id: number; predicted: PredictedCorners }>;
  r0RecordedReference: R0RecordedReference | null;
};

export type LaneData = Array<Array<number | null>>;

export type MultiRunOverlayOk = {
  ok: true;
  data: { lf: LaneData; rf: LaneData; lr: LaneData; rr: LaneData };
  yDomains: { lf: [number, number]; rf: [number, number];
              lr: [number, number]; rr: [number, number] };
};

export type MultiRunOverlayErr =
  | { ok: false; reason: "length_mismatch" };

export type MultiRunOverlayResult = MultiRunOverlayOk | MultiRunOverlayErr;

const CORNERS: CornerKey[] = ["lf", "rf", "lr", "rr"];
const Y_PADDING_FRAC = 0.1;
const M_TO_MM = 1000;

function sanitizeMm(arr: number[]): Array<number | null> {
  return arr.map((v) => (Number.isFinite(v) ? v * M_TO_MM : null));
}

function unionDomain(arrays: Array<Array<number | null>>): [number, number] {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const arr of arrays) {
    for (const v of arr) {
      if (v == null || !Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return [0, 1];
  }
  const span = hi - lo;
  const pad = span === 0 ? Math.abs(lo) * Y_PADDING_FRAC || 0.01
                          : span * Y_PADDING_FRAC;
  return [lo - pad, hi + pad];
}

export function buildMultiRunOverlayData(
  input: MultiRunOverlayInput,
): MultiRunOverlayResult {
  const { time_s, runs, r0RecordedReference } = input;
  const N = time_s.length;

  for (const r of runs) {
    for (const corner of CORNERS) {
      if (r.predicted[corner].length !== N) {
        return { ok: false, reason: "length_mismatch" };
      }
    }
  }
  if (r0RecordedReference != null) {
    if (r0RecordedReference.time_s.length !== N) {
      return { ok: false, reason: "length_mismatch" };
    }
    for (const corner of CORNERS) {
      if (r0RecordedReference.recorded[corner].length !== N) {
        return { ok: false, reason: "length_mismatch" };
      }
    }
  }

  const data = { lf: [], rf: [], lr: [], rr: [] } as MultiRunOverlayOk["data"];
  const yDomains = {} as MultiRunOverlayOk["yDomains"];

  for (const corner of CORNERS) {
    const lane: LaneData = [time_s.slice()];
    for (const r of runs) {
      lane.push(sanitizeMm(r.predicted[corner]));
    }
    if (r0RecordedReference != null) {
      lane.push(sanitizeMm(r0RecordedReference.recorded[corner]));
    }
    data[corner] = lane;
    yDomains[corner] = unionDomain(lane.slice(1));
  }

  return { ok: true, data, yDomains };
}
